import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { COUNTRY } from '../constant/country';
import { LANGUAGE } from '../constant/language';
import { getCountryLabel, getLanguageLabel, toBcp47 } from '../lib/displayNames';
import { flattenManuals, getPublishedApps } from '../lib/manuals';
import { handleManualDownload } from '../lib/manualUtils';
import { getExceptions, getGeoCountry } from '../lib/visibility';
import CriticalNoticeModal from './CriticalNoticeModal';
import EifuHeader from './EifuHeader';

const selectControlStyles = {
  control: (styles) => ({
    ...styles,
    border: '1px solid rgba(8, 60, 130, 0.07)',
    height: '42px',
    backgroundColor: '#fefefe',
    fontSize: '0.75rem',
    fontWeight: '500 !important',
    color: '#60697b !important',
    borderRadius: '0.4rem',
    boxShadow: '0rem 0rem 1.25rem rgb(30 34 40 / 4%)',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;',
    minWidth: '200px',
  }),
};

const AppManuals = () => {
  const { t, i18n } = useTranslation(['translation']);
  const [loading, setLoading] = useState(true);
  const [checkedApps, setCheckedApps] = useState(false);
  const [appList, setAppList] = useState([]);
  const [docList, setDocList] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [keywords, setKeywords] = useState({});
  // Visitor's country (header-level setting) drives which manuals are shown via
  // exception rules. The default is auto-detected from the Cloudflare edge on
  // every load (see effect below); a manual pick overrides it for the session.
  const [country, setCountry] = useState('');

  const onCountryChange = useCallback((code) => {
    setCountry(code || '');
    // A different country can change which manual languages are available.
    setKeywords((prev) => ({ ...prev, language: undefined }));
  }, []);

  const appsTitleList = useMemo(
    () => appList?.map((x) => ({ value: x.name, label: x.title || x.name })),
    [appList]
  );

  // Visitor picks their country from the full ISO list. With no matching
  // exception a country sees everything (exceptions are deviations only).
  // Names come from the browser's CLDR data so the list reads in the selected UI
  // language; sorting follows that language's collation, not ASCII order.
  const countryOptions = useMemo(
    () =>
      COUNTRY.map((c) => ({
        value: c.code,
        label: getCountryLabel(c.code, i18n.language, t(c.label || c.code)),
      })).sort((a, b) => a.label.localeCompare(b.label, toBcp47(i18n.language))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  // Filter by selected product first.
  const softwareFilteredList = useMemo(() => {
    let _list = docList;
    if (keywords?.name) {
      _list = _list.filter((x) => x._app_info?.name === keywords?.name);
    }
    return _list;
  }, [docList, keywords?.name]);

  // Apply country exceptions: hide a SW entirely, or restrict it to specific
  // document versions (revisions). hide wins; version allow-lists union.
  const countryFilteredList = useMemo(() => {
    if (!country) {
      return softwareFilteredList;
    }
    const rules = exceptions.filter(
      (r) => Array.isArray(r.countries) && r.countries.includes(country)
    );
    const hiddenApps = new Set(
      rules.filter((r) => r.mode === 'hide').map((r) => r.appName)
    );
    const allowedVersions = {};
    rules
      .filter((r) => r.mode === 'versions')
      .forEach((r) => {
        if (!allowedVersions[r.appName]) {
          allowedVersions[r.appName] = new Set();
        }
        (r.versions || []).forEach((v) => allowedVersions[r.appName].add(String(v)));
      });
    return softwareFilteredList.filter((x) => {
      const app = x._app_info?.name;
      if (hiddenApps.has(app)) {
        return false;
      }
      if (allowedVersions[app] && !allowedVersions[app].has(String(x.revision))) {
        return false;
      }
      return true;
    });
  }, [softwareFilteredList, country, exceptions]);

  // Language options reflect the country-filtered set (language is a finder, not
  // a visibility axis).
  const availableLanguages = useMemo(() => {
    const languageSet = new Set();
    countryFilteredList.forEach((x) => {
      if (x.manualLang) {
        languageSet.add(x.manualLang);
      }
    });
    return Array.from(languageSet)
      .map((langCode) => {
        const langInfo = LANGUAGE.find((y) => y.code === langCode);
        return {
          value: langCode,
          label: getLanguageLabel(
            langCode,
            i18n.language,
            t(langInfo?.label || langCode.toUpperCase())
          ),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, toBcp47(i18n.language)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryFilteredList, t, i18n.language]);

  const displayList = useMemo(() => {
    let _list = countryFilteredList;
    if (keywords?.fileName?.trim()?.length > 1) {
      const _fileName = keywords?.fileName?.trim()?.toLowerCase();
      _list = _list.filter((x) => x.doc?.file_name?.toLowerCase()?.includes(_fileName));
    }
    if (keywords?.language) {
      _list = _list.filter((x) => x.manualLang === keywords?.language);
    }
    return _list;
  }, [countryFilteredList, keywords?.fileName, keywords?.language]);

  useEffect(() => {
    getExceptions().then(setExceptions);
  }, []);

  // First-load default: preselect the country detected at the Cloudflare edge.
  // Runs on every load so the default always reflects the visitor's location; a
  // manual pick afterwards overrides it for the session.
  useEffect(() => {
    getGeoCountry().then((code) => {
      if (code && COUNTRY.some((c) => c.code === code)) {
        setCountry(code);
      }
    });
  }, []);

  useEffect(() => {
    getPublishedApps()
      .then((items) => {
        setAppList(_.orderBy(items, ['title'], ['asc']));
        setCheckedApps(true);
      })
      .catch(() => {
        setAppList([]);
        setCheckedApps(true);
      });
  }, []);

  const getAllManuals = useCallback((apps) => {
    try {
      setDocList(flattenManuals(apps));
    } catch (error) {
      console.log('ERROR[getAllManuals]', error?.toString());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (checkedApps) {
      setLoading(true);
      getAllManuals(appList);
    }
  }, [checkedApps, getAllManuals, appList]);

  // Anonymous download — no user info attached.
  const onManualDownload = useCallback((e, directLink, fileName) => {
    handleManualDownload(e, directLink, fileName);
  }, []);

  const getManualLang = (doc, lang) => (
    <div>
      <a
        href={doc.direct_link}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => onManualDownload(e, doc.direct_link, doc.file_name)}
        className="text-decoration-none d-inline-flex align-items-center gap-1"
      >
        {lang && (
          <Badge bg="primary">
            {getLanguageLabel(
              lang,
              i18n.language,
              t(LANGUAGE.find((y) => y.code === lang)?.label || lang.toUpperCase())
            )}
          </Badge>
        )}
        {doc.file_name}
        <Badge bg="secondary">{doc.file_name.trim().split('.').at(-1).toUpperCase()}</Badge>
      </a>
    </div>
  );

  return (
    <>
      <CriticalNoticeModal />
      <EifuHeader country={country} onCountryChange={onCountryChange} countryOptions={countryOptions} />
      <section
        className="wrapper app-ban-area d-flex"
        data-image-src="https://assets.rayplatform.com/rayteams-homepage/assets/img/img_app_ban01.jpg"
        style={{
          backgroundImage:
            'url(https://assets.rayplatform.com/rayteams-homepage/assets/img/img_app_ban01.jpg)',
          height: '400px',
        }}
      >
        <div className="container align-items-center d-flex">
          <div className="row pt-md-12">
            <div className="col-lg-12 z-index-9">
              <h2 className="display-3 mb-6 fw-normal text-white">eIFU - {t('show all manuals')}</h2>
              <div className="fs-lg mb-6 text-white opacity-75">
                <div className="fs-15">{t('eifuPortalIntro')}</div>
              </div>
              <div />
            </div>
          </div>
        </div>
      </section>
      <section className="wrapper bg-apps apps-list">
        <div className="container py-12">
          {loading && <>{t('Checking the latest documents.')}</>}
          {!loading && (
            <>
              {docList?.length === 0 && <div>{t('There are no manuals to display.')}</div>}
              {docList.length > 0 && (
                <>
                  <div className="d-flex align-items-center justify-content-between">
                    <h3 className="mb-0">{t('Manuals')}</h3>
                    <div className="d-flex align-items-center gap-2" data-testid="eifu-filters">
                      <Select
                        options={appsTitleList}
                        value={appsTitleList.find((x) => x.value === keywords.name) || ''}
                        onChange={(_data) =>
                          setKeywords((prev) => ({
                            ...prev,
                            name: _data?.value,
                            language: undefined,
                          }))
                        }
                        placeholder={t('Product Name')}
                        isClearable
                        styles={{
                          control: (styles) => ({ ...selectControlStyles.control(styles), minWidth: '300px' }),
                        }}
                      />
                      <Select
                        options={availableLanguages}
                        value={availableLanguages.find((x) => x.value === keywords.language) || ''}
                        onChange={(_data) => setKeywords((prev) => ({ ...prev, language: _data?.value }))}
                        placeholder={t('Language')}
                        isClearable
                        isDisabled={availableLanguages.length === 0}
                        styles={selectControlStyles}
                      />
                      <input
                        type="text"
                        value={keywords?.fileName || ''}
                        onChange={(e) => setKeywords((prev) => ({ ...prev, fileName: e?.target?.value }))}
                        placeholder={t('Document Name')}
                        className="form-control form-control-sm"
                      />
                    </div>
                  </div>
                  <div className="alert alert-info mt-3 mb-3" role="alert">
                    {t('appManuals.downloadHelpText')}
                    <div className="small">{t('appManuals.systemRequirements')}</div>
                  </div>
                  <table className="table table-order mt-2" style={{ borderTop: '1px solid #8574FF' }}>
                    <thead>
                      <tr>
                        <th>{t('Product')}</th>
                        <th>{t('Revision')}</th>
                        <th>{t('Document')}</th>
                        <th>{t('Published')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayList.map((x) => (
                        <tr key={x.file_name}>
                          <td>{x._app_info?.title || x._app_info?.name}</td>
                          <td>{x?.revision}</td>
                          <td>
                            {x.doc?.direct_link && getManualLang(x.doc, x.manualLang)}
                            {x.docs?.length > 0 && x.docs.map((y) => getManualLang(y, x.manualLang))}
                          </td>
                          <td>{x.published ? moment(x.published).format('LL') : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default AppManuals;
